/**
 * Spec 023: the "Bookings" card on `/admin/brand-settings`. Whether this site takes
 * bookings yet; only the platform owner changes it (the server refuses brand admins).
 * Plain functions, tested in `bookingsOpenAdmin.test.ts`.
 */

type Rules = Record<string, unknown> | null | undefined;

/** A brand that never set it takes bookings. */
export function bookingsOpenOf(rules: Rules): boolean {
  return rules?.bookingsOpen !== false;
}

export function bookingsStatusLabel(open: boolean): string {
  return open ? 'Open' : 'Not open yet';
}

/** History words: "Bookings: open → not open". */
export function describeBookingsOpenValue(value: unknown): string {
  return value === false ? 'not open' : 'open';
}

export interface BookingsOpenConfirm {
  title: string;
  body: string;
  confirm: string;
}

/** What the platform owner is asked before the switch is saved. */
export function bookingsOpenConfirm(nextOpen: boolean, brandName: string): BookingsOpenConfirm {
  return nextOpen
    ? {
        title: `Open bookings on ${brandName}?`,
        body: 'Guests will be able to pay and book trips here. Turn this on only after insurance, the Stripe account and support are ready.',
        confirm: 'Open bookings',
      }
    : {
        title: `Close bookings on ${brandName}?`,
        body: "Guests can still see cars and the checkout page, but they can't pay. Trips already booked go ahead. Other brands stop listing cars that can only be booked here.",
        confirm: 'Close bookings',
      };
}

/** The count line under a closed brand's switch; null while open or unknown. */
export function refusedCheckoutsText(
  count: number | null | undefined,
  open: boolean,
): string | null {
  if (open || count === null || count === undefined) return null;
  return count === 1
    ? '1 checkout stopped since closed'
    : `${count} checkouts stopped since closed`;
}
